// ============================================================
// THE DISPLAY CAMERA'S MOTION - how it travels to a shot (`camera.ts` says
// which), and how it breathes at rest (plan map-display, Track C).
//
// The user's rulings (2026-10-07): a move is eased; yaw always turns the
// short way; a reverse shot (a turn past 90°) is a CRANE - the camera rises
// to about 58° as it turns, then settles, in about 1.1 s - because a cut
// read as a teleport. At rest the camera breathes, a slow small drift that
// restarts at phase 0 from wherever it settled and fades in, or it would
// jump after every move. Reduced motion: every move is instant and nothing
// breathes.
//
// Pure. Zero React dependency; the clock is the caller's (`now` in ms).
// ============================================================
import type { Camera } from '../battlefield/projection';
import { easeInOut, lerp, lerpYaw, progress, yawDelta } from './timeline';

/** A turn wider than this is a reverse shot: a crane. */
const REVERSE = 90;
const MOVE_MS = 700;
const CRANE_MS = 1100;
/** How high a crane rises at the middle of its turn, in degrees of pitch. */
export const CRANE_PITCH = 58;
/**
 * The breathing drift, as the user saw it in the Focus demo: ±3°, a period
 * of about 19 s (one radian every 3 s), fading in over 2 s once the camera
 * has rested 2.5 s.
 */
export const BREATH_DEG = 3;
const BREATH_RADIAN_MS = 3000;
const BREATH_FADE_MS = 2000;
export const BREATH_DELAY_MS = 2500;
/**
 * A camera left at rest this long stops breathing, holding where it is: every
 * breath redraws the whole map, and an idle table should cost nothing. Ten
 * seconds, not the minute it was: a minute of breathing after every move kept
 * a laptop's main thread at 100% (plan perf-overhaul, Stage 1). A hidden tab
 * draws no frames at all (the clock is requestAnimationFrame).
 */
export const BREATH_FOR_MS = 10_000;

/** One move: from a camera to a shot, starting at `start` and lasting `ms`. */
export interface CameraMove {
  from: Camera;
  to: Camera;
  start: number;
  ms: number;
  crane: boolean;
}

/** The move to `to`: a crane for a reverse shot, instant under reduced motion. */
export function planMove(from: Camera, to: Camera, start: number, reduced: boolean): CameraMove {
  const crane = Math.abs(yawDelta(from.yaw, to.yaw)) > REVERSE;
  return { from, to, start, ms: reduced ? 0 : crane ? CRANE_MS : MOVE_MS, crane };
}

/** A crane's pitch: the straight line between the two, lifted towards CRANE_PITCH at the middle. */
function pitchAt(move: CameraMove, k: number): number {
  const line = lerp(move.from.pitch, move.to.pitch, k);
  if (!move.crane || line >= CRANE_PITCH) return line;
  return line + (CRANE_PITCH - line) * Math.sin(Math.PI * k);
}

/**
 * The camera at `now` along the move. The zoom eases in ratio (geometric),
 * so the second half of a long zoom covers what the first did. A lens that
 * comes or goes eases from the flat camera (`fov` 0).
 */
export function cameraAt(move: CameraMove, now: number): Camera {
  const { from, to } = move;
  const k = easeInOut(progress(now, move.start, move.ms));
  if (k >= 1) return to;
  return {
    ...to,
    yaw: lerpYaw(from.yaw, to.yaw, k),
    pitch: pitchAt(move, k),
    zoom: from.zoom * Math.pow(to.zoom / from.zoom, k),
    target: { x: lerp(from.target.x, to.target.x, k), z: lerp(from.target.z, to.target.z, k) },
    lift: lerp(from.lift ?? 0, to.lift ?? 0, k),
    fov: lerp(from.fov ?? 0, to.fov ?? 0, k),
  };
}

/**
 * The camera at rest, `since` ms after it settled: still a while, then a slow
 * yaw drift fading in from phase 0. (A screen that holds still never asks.)
 */
export function breathe(camera: Camera, since: number): Camera {
  const t = since - BREATH_DELAY_MS;
  if (t <= 0) return camera;
  const fade = Math.min(1, t / BREATH_FADE_MS);
  return { ...camera, yaw: camera.yaw + BREATH_DEG * fade * Math.sin(t / BREATH_RADIAN_MS) };
}
